/**
 * Withdrawal Submit Handler
 * 
 * Handles the withdrawal submission process when user clicks the withdraw button.
 * 
 * @package betbeast
 */

import { showCustomHint } from '../custom-hint.js';
import { writeJourney } from '../../modules/payment-journey.js';

export class WithdrawalSubmitHandler {
    constructor(modalElement, flowController, schemaRenderer = null) {
        this.modal = modalElement;
        this.flowController = flowController;
        this.schemaRenderer = schemaRenderer;
        this.submitButton = null;
        this.amountInput = null;
        
        if (!this.modal || !this.flowController) {
            console.warn('Modal element or flow controller not provided to WithdrawalSubmitHandler');
            return;
        }
        
        this.init();
    }
    
    /**
     * Initialize submit handler
     */
    init() {
        this.submitButton = this.modal.querySelector('[data-action="initiate-withdrawal"]');
        this.amountInput = this.modal.querySelector('.wallet-withdrawal-amount-input');
        
        if (!this.submitButton) {
            console.warn('Withdrawal submit button not found');
            return;
        }
        
        // Bind click event
        this.submitButton.addEventListener('click', () => this.handleSubmit());

        // Bind "Back" button in the payment/iframe step
        const backBtn = this.modal.querySelector('[data-action="back-to-amount"]');
        if (backBtn) {
            backBtn.addEventListener('click', () => this.handleBackToAmount());
        }

        // See the deposit handler: Back from the provider's page can restore this one
        // from the browser's cache with the submit button still spinning.
        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                this.setLoadingState(false);
            }
        });
    }
    
    /**
     * Handle withdrawal submission
     */
    async handleSubmit() {
        console.log('=== Withdrawal Submit Clicked ===');
        
        // Get selected payment method
        const paymentMethod = this.flowController.getSelectedPaymentMethod();
        if (!paymentMethod) {
            this.showError('Please select a withdrawal method');
            return;
        }

        // Variant of the API method (CPT post id), so the server applies this card's schema.
        const activeButton = this.modal.querySelector('.wallet-withdrawal-payment-method-item.is-active');
        const variantKey = activeButton?.dataset.variantKey || '';
        
        // Get withdrawal amount
        const amount = parseFloat(this.amountInput?.value);
        if (!amount || amount <= 0) {
            this.showError('Please enter a valid withdrawal amount');
            return;
        }
        
        // Validate amount against limits. validateAmount() returns an object, so
        // testing it for truthiness never blocks — use validateBeforeSubmit(), which
        // reports the specific breach and renders the inline field error too.
        const validator = this.flowController.getValidator();
        if (validator) {
            const validation = validator.validateBeforeSubmit();
            if (!validation.isValid) {
                this.showError(validation.error);
                return;
            }
        }

        // Validate schema-defined extra fields
        if (this.schemaRenderer && this.schemaRenderer.hasFields()) {
            const schemaValidation = this.schemaRenderer.validate();
            if (!schemaValidation.valid) {
                this.showError(schemaValidation.message);
                return;
            }
        }

        // Read selected saved card (radio button) if a card method is active.
        const selectedCardRadio = this.modal.querySelector('[name="saved_card"]:checked');
        const paymentInformationId = selectedCardRadio ? selectedCardRadio.value : null;

        // APCOPAY_V2_CARD requires a saved card (paymentInformationId). Block early with a
        // clear message instead of letting the API reject a null after the round-trip.
        // Keyed on the literal code because it's the only CREDIT_CARD method today; if more
        // are added, switch to a payment-type check (CREDIT_CARD) instead.
        if (paymentMethod === 'APCOPAY_V2_CARD' && !paymentInformationId) {
            const hasCards = !!this.modal.querySelector('[name="saved_card"]');
            this.showError(hasCards
                ? 'Please select a card for this withdrawal.'
                : 'You need a saved card to withdraw to a credit card. Make a card deposit first.');
            return;
        }

        // Show loading state
        this.setLoadingState(true);

        try {
            // Get handler and process withdrawal
            const handler = this.flowController.getHandler();
            if (!handler) {
                throw new Error('Withdrawal handler not initialized');
            }
            
            // Read the selected saved payment entry (card / bank / crypto / wallet).
            // The "__new__" sentinel means the player chose to enter new details, so
            // there is no saved id and the schema extra fields carry the details.
            const selectedCardRadio = this.modal.querySelector('[name="saved_card"]:checked');
            const paymentInformationId = (selectedCardRadio && selectedCardRadio.value !== '__new__')
                ? selectedCardRadio.value
                : null;
            const conversionId = null;

            // Collect extra fields defined by the method's validation schema
            const extraFields = this.schemaRenderer ? this.schemaRenderer.collectValues() : {};
            
            const result = await handler.processWithdrawal(
                paymentMethod,
                amount,
                paymentInformationId,
                conversionId,
                extraFields,
                variantKey
            );
            
            console.log('=== Withdrawal Success ===');
            console.log('Payment ID:', result.paymentId);
            console.log('Continue URL:', result.continueUrl);

            // Leaving the site for the provider's own page. Navigate straight from the
            // amount step: switching to the payment step first would flash an empty
            // panel on the way out, and the button's loading state is the right thing
            // to leave on screen while the browser moves.
            //
            // assign(), not replace(): it keeps the cashier in this page's history, so
            // Back from the provider's page brings the player here rather than off the
            // site entirely — the one cheap way to change their mind. The payment
            // already exists on the server either way, so returning does no harm.
            //
            // No #withdrawal on the address: inside the frame that hash told the result page
            // which flow it belonged to, and the provider's own redirect would drop it
            // anyway. The fork written above carries that now.
            if (result.leavesSite && result.continueUrl) {
                // Mirror the fork the server wrote as a cookie, for a return where the
                // cookie does not survive. Written here rather than on `leavesSite`
                // alone: a method that leaves can still answer with a finished payment
                // and no page to go to, and a fork nobody comes back for would sit in
                // storage colouring a later visit.
                writeJourney('withdrawal', result.paymentId);

                window.location.assign(result.continueUrl);
                return;
            }

            // Transition to payment step (same pattern as deposit)
            const amountSection  = this.modal.querySelector('[data-step="amount"]');
            const paymentSection = this.modal.querySelector('[data-step="payment"]');

            if (amountSection)  amountSection.style.display  = 'none';
            if (paymentSection) paymentSection.style.display = 'block';

            // Hide security section
            const securitySection = this.modal.querySelector('.wallet-withdrawal-security-section');
            if (securitySection) securitySection.style.display = 'none';

            // Hide submit button section
            const submitSection = this.modal.querySelector('.wallet-withdrawal-submit-section');
            if (submitSection) submitSection.style.display = 'none';

            // Expand to full modal width (hide left column)
            const content = this.modal.querySelector('.wallet-withdrawal-modal__content');
            if (content) content.classList.add('wallet-withdrawal-modal__content--iframe-mode');

            if (result.continueUrl) {
                // PSP redirect flow — load in iframe
                this.loadPaymentIframe(result.continueUrl, result.paymentId);
            } else {
                // Direct-success flow (status: SUCCESS, no continueUrl) — show inline result
                this.showInlineResult(result);
            }
            
        } catch (error) {
            console.error('Withdrawal Error:', error);
            this.showError(error.message || 'Failed to process withdrawal. Please try again.');
        } finally {
            this.setLoadingState(false);
        }
    }
    
    /**
     * Load the PSP page inside an iframe within the payment step.
     *
     * @param {string} continueUrl  URL returned by the API
     * @param {string} paymentId    Payment ID for tracking
     */
    loadPaymentIframe(continueUrl, paymentId) {
        const iframeContainer = this.modal.querySelector('.wallet-withdrawal-iframe-container');
        const placeholder     = this.modal.querySelector('.wallet-withdrawal-iframe-placeholder');

        if (!iframeContainer) {
            console.error('Withdrawal iframe container not found');
            return;
        }

        if (placeholder) placeholder.style.display = 'none';

        // Append #withdrawal so the result page (payment-result.js) knows which
        // flow it belongs to and sends the correct postMessage types.
        const iframeSrc = continueUrl + ( continueUrl.includes('#') ? '' : '#withdrawal' );
        const iframe = document.createElement('iframe');
        iframe.src = iframeSrc;
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('allowfullscreen', 'true');
        iframe.setAttribute('data-payment-id', paymentId);

        // After the iframe loads, also send a postMessage in case the PSP's
        // server-side redirect stripped the hash.
        iframe.addEventListener('load', () => {
            try {
                iframe.contentWindow.postMessage(
                    { type: 'payment_result_init', flow: 'withdrawal' },
                    '*'
                );
            } catch ( e ) { /* cross-origin — hash is the primary signal */ }
        });

        iframeContainer.innerHTML = '';
        iframeContainer.appendChild(iframe);

        console.log('✅ Withdrawal iframe loaded for payment ID:', paymentId);
    }

    /**
     * Show the inline result panel (used when API returns SUCCESS with no continueUrl).
     *
     * @param {Object} result  API result: { status, paymentId, continueUrl }
     */
    showInlineResult(result) {
        const inlineResult = this.modal.querySelector('[data-result-inline]');
        if (!inlineResult) {
            console.error('Withdrawal inline result container not found');
            return;
        }

        const status    = (result.status || '').toUpperCase();
        let stateKey;
        if ( status === 'SUCCESS' || status === 'PROCESSED' ) {
            stateKey = 'success';
        } else if ( status === 'PENDING' ) {
            stateKey = 'pending';
        } else {
            stateKey = 'error';
        }
        const paymentId = result.paymentId || '';

        // Show container, hide iframe container
        inlineResult.style.display = 'block';
        const iframeContainer = this.modal.querySelector('.wallet-withdrawal-iframe-container');
        if (iframeContainer) iframeContainer.style.display = 'none';

        // Hide iframe-specific chrome (title + back button)
        const iframeTitle = this.modal.querySelector('.wallet-withdrawal-iframe-title');
        if (iframeTitle) iframeTitle.style.display = 'none';
        const iframeActions = this.modal.querySelector('.wallet-withdrawal-iframe-actions');
        if (iframeActions) iframeActions.style.display = 'none';

        const block   = inlineResult.querySelector('[data-withdrawal-result]');
        const stateEl = block ? block.querySelector(`[data-result-state="${stateKey}"]`) : null;
        if (stateEl) {
            stateEl.style.display = 'flex';

            const detailMap = {
                status:    status    || '—',
                reference: paymentId || '—',
                pspid:     '—',
            };
            Object.entries(detailMap).forEach(([key, value]) => {
                const row = stateEl.querySelector(`[data-detail="${key}"] [data-detail-value]`);
                if (row) row.textContent = value;
            });

            // Hide empty rows
            stateEl.querySelectorAll('[data-detail]').forEach(row => {
                const val = row.querySelector('[data-detail-value]');
                if (val && val.textContent === '—') row.style.display = 'none';
            });

            // "Try Again" goes back to amount step
            const tryAgainBtn = stateEl.querySelector('[data-try-again]');
            if (tryAgainBtn) {
                tryAgainBtn.addEventListener('click', () => this.handleBackToAmount(), { once: true });
            }
        }

        console.log(`✅ Inline withdrawal result shown — state: ${stateKey}, paymentId: ${paymentId}`);
    }

    /**
     * Return to the amount/method step from the payment iframe or inline result step.
     */
    handleBackToAmount() {
        const amountSection  = this.modal.querySelector('[data-step="amount"]');
        const paymentSection = this.modal.querySelector('[data-step="payment"]');

        if (amountSection)  amountSection.style.display  = 'block';
        if (paymentSection) paymentSection.style.display = 'none';

        // Restore security section
        const securitySection = this.modal.querySelector('.wallet-withdrawal-security-section');
        if (securitySection) securitySection.style.display = '';

        // Restore submit section
        const submitSection = this.modal.querySelector('.wallet-withdrawal-submit-section');
        if (submitSection) submitSection.style.display = '';

        // Restore two-column layout
        const content = this.modal.querySelector('.wallet-withdrawal-modal__content');
        if (content) content.classList.remove('wallet-withdrawal-modal__content--iframe-mode');

        // Restore iframe-specific chrome (title + back button)
        const iframeTitle = this.modal.querySelector('.wallet-withdrawal-iframe-title');
        if (iframeTitle) iframeTitle.style.display = '';
        const iframeActions = this.modal.querySelector('.wallet-withdrawal-iframe-actions');
        if (iframeActions) iframeActions.style.display = '';

        // Clear iframe
        const iframeContainer = this.modal.querySelector('.wallet-withdrawal-iframe-container');
        const placeholder     = this.modal.querySelector('.wallet-withdrawal-iframe-placeholder');
        if (iframeContainer) {
            iframeContainer.innerHTML = '';
            iframeContainer.style.display = '';
            if (placeholder) {
                iframeContainer.appendChild(placeholder);
                placeholder.style.display = 'flex';
            }
        }

        // Reset inline result panel
        const inlineResult = this.modal.querySelector('[data-result-inline]');
        if (inlineResult) {
            inlineResult.style.display = 'none';
            inlineResult.querySelectorAll('[data-result-state]').forEach(el => {
                el.style.display = 'none';
            });
        }

        this.setLoadingState(false);
        console.log('Returned to withdrawal amount selection');
    }

    /**
     * Set loading state on submit button
     * @param {boolean} isLoading
     */
    setLoadingState(isLoading) {
        if (!this.submitButton) return;
        
        const buttonText = this.submitButton.querySelector('.wallet-withdrawal-submit-btn-text');
        const buttonLoader = this.submitButton.querySelector('.wallet-withdrawal-submit-btn-loader');
        
        if (isLoading) {
            this.submitButton.disabled = true;
            if (buttonText) buttonText.style.display = 'none';
            if (buttonLoader) buttonLoader.style.display = 'inline-block';
        } else {
            this.submitButton.disabled = false;
            if (buttonText) buttonText.style.display = 'inline';
            if (buttonLoader) buttonLoader.style.display = 'none';
        }
    }
    
    /**
     * Show error message using custom hint
     * @param {string} message
     */
    showError(message) {
        showCustomHint(message, 'error');
        console.error('Withdrawal Error:', message);
    }
    
    /**
     * Show success message using custom hint
     * @param {string} message
     */
    showSuccess(message) {
        showCustomHint(message, 'success');
        console.log('Withdrawal Success:', message);
    }
}
